import type { ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { clsx } from 'clsx'
import { Icon, type IconName } from '@/shared/components/Icon'
import { Tooltip } from '@/shared/components/Tooltip'
import { useT } from '@/shared/i18n'

// ---------------------------------------------------------------------------
// Sidebar — presentational navigation sidebar.
//
// Renders a grouped nav list using .sidebar / .sidebar__group /
// .sidebar__item / .sidebar__count classes from shared/styles/components.css.
//
// Props:
//   groups[]   — nav groups, each with a label and list of items
//   activeKey  — key of the currently active item; drives .sidebar__item--active
//                and aria-current="page"
//
// Items render as Inertia <Link>s: they are still real anchors (keyboard-
// navigable and crawlable) but Inertia intercepts the click for an in-place SPA
// visit. A plain <a> would trigger a full document navigation on every click —
// the whole UI reloads with a white flash instead of swapping the page region.
// ---------------------------------------------------------------------------

export type SidebarItem = {
  /** Unique key — matched against activeKey to determine the active state. */
  key: string
  /** Display label for the nav item (already i18n'd by the caller). */
  label: string
  /** Optional icon from the shared Icon registry. */
  icon?: IconName
  /** Optional numeric badge count rendered as a pill at the far right. */
  count?: number
  /** Navigation target. */
  href: string
}

export type SidebarGroup = {
  /** Group heading label (already i18n'd by the caller). */
  label: string
  items: SidebarItem[]
}

export type SidebarProps = {
  groups: SidebarGroup[]
  activeKey: string
  /**
   * Icon-only rail. The labels stay in the markup (visually hidden by the
   * shell's narrow-nav CSS) so the accessible name survives; this only adds a
   * hover tooltip, which is the sighted user's only way back to the label.
   */
  compact?: boolean
  /** Optional slot pinned below the nav groups (e.g. a collapse toggle). */
  footer?: ReactNode
}

/**
 * Presentational sidebar navigation primitive.
 *
 * Renders a <nav> containing grouped lists of anchor links. Each item may
 * carry an icon and a count badge. The item matching activeKey receives the
 * .sidebar__item--active class and aria-current="page".
 *
 * Usage:
 *   <Sidebar
 *     groups={[
 *       {
 *         label: t('nav.groups.cases'),
 *         items: [
 *           { key: 'inbox', label: t('nav.inbox'), icon: 'inbox', count: 3, href: '/inbox' },
 *         ],
 *       },
 *     ]}
 *     activeKey={currentPage}
 *   />
 */
export function Sidebar({ groups, activeKey, compact = false, footer }: SidebarProps) {
  const t = useT()

  return (
    <nav className="sidebar" aria-label={t('chrome.nav.label')}>
      {groups.map((group) => (
        <div key={group.label}>
          <div className="sidebar__group">{group.label}</div>
          {group.items.map((item) => {
            const isActive = item.key === activeKey
            return (
              <Tooltip key={item.key} label={compact ? item.label : ''} delay={250}>
                <Link
                  href={item.href}
                  className={clsx(
                    'sidebar__item',
                    isActive && 'sidebar__item--active',
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.icon != null && (
                    <Icon name={item.icon} size={16} aria-hidden="true" />
                  )}
                  <span className="sidebar__label">{item.label}</span>
                  {item.count != null && (
                    <span className="sidebar__count">{item.count}</span>
                  )}
                </Link>
              </Tooltip>
            )
          })}
        </div>
      ))}
      {footer != null && <div className="sidebar__footer">{footer}</div>}
    </nav>
  )
}
